"use client";

import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { type Editor } from "@tiptap/react";

import { RichTextToolbar } from "./RichTextToolbar";

type ContentLinkSelection = {
  contentSlug?: string;
  contentId?: string;
};

type RichTextToolbarDockProps = {
  open: boolean;
  editor: Editor | null;
  onRequestLink: (prevHref?: string) => void;
  onRequestContentLink: (prev?: ContentLinkSelection) => void;
};

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 767px)");
    const handleChange = (event: MediaQueryListEvent) =>
      setIsSmall(event.matches);

    setIsSmall(mq.matches);

    if ("addEventListener" in mq) {
      mq.addEventListener("change", handleChange);
      return () => mq.removeEventListener("change", handleChange);
    }

    const legacyMq = mq as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };

    legacyMq.addListener?.(handleChange);
    return () => {
      legacyMq.removeListener?.(handleChange);
    };
  }, []);

  return isSmall;
}

function useKeyboardAwareDock({
  enabled,
  bottomMargin = 8,
}: {
  enabled: boolean;
  bottomMargin?: number;
}) {
  const [state, setState] = React.useState({
    keyboardOpen: false,
    keyboardHeight: 0,
  });

  React.useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const visualViewport = window.visualViewport;
    const update = () => {
      if (!visualViewport) return;

      const keyboardHeight = Math.max(
        0,
        window.innerHeight -
          visualViewport.height -
          (visualViewport.offsetTop || 0),
      );

      setState({
        keyboardOpen: keyboardHeight > 80,
        keyboardHeight,
      });
    };

    update();
    visualViewport?.addEventListener("resize", update);
    visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      visualViewport?.removeEventListener("resize", update);
      visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [enabled]);

  const dockStyle = React.useMemo<React.CSSProperties>(() => {
    if (!enabled) return {};

    const safeArea = "env(safe-area-inset-bottom)";
    const bottom = state.keyboardOpen
      ? `calc(${state.keyboardHeight}px + ${bottomMargin}px + ${safeArea})`
      : `calc(${bottomMargin}px + ${safeArea})`;

    return { bottom };
  }, [enabled, state.keyboardHeight, state.keyboardOpen, bottomMargin]);

  return dockStyle;
}

export function RichTextToolbarDock({
  open,
  editor,
  onRequestLink,
  onRequestContentLink,
}: RichTextToolbarDockProps) {
  const isSmall = useIsSmallScreen();
  const dockStyle = useKeyboardAwareDock({
    enabled: open && isSmall,
    bottomMargin: 8,
  });
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.aside
          key="richTextToolbarDock"
          data-richtext-dock="true"
          data-safe-nav="true"
          className={[
            "fixed z-[70] flex items-center justify-center pointer-events-auto",
            isSmall
              ? "left-1/2 w-[96%] max-w-[min(100%,420px)] -translate-x-1/2 px-2"
              : "top-1/3 right-4",
          ].join(" ")}
          initial={isSmall ? { y: 40, opacity: 0 } : { x: 40, opacity: 0 }}
          animate={isSmall ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
          exit={isSmall ? { y: 40, opacity: 0 } : { x: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          style={isSmall ? dockStyle : undefined}
        >
          <div className="border-grayCustom flex flex-row items-center gap-1 rounded-xl border bg-white/95 px-2 py-2 shadow-2xl backdrop-blur sm:flex-col">
            <RichTextToolbar
              editor={editor}
              onRequestLink={onRequestLink}
              onRequestContentLink={onRequestContentLink}
            />
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
